import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Helper to create client with correct auth token
const getPortalClient = () => {
  const token = localStorage.getItem("portalToken");
  const client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  // Response interceptor to handle invalid/expired token or unauthorized errors
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      const message = error?.response?.data?.message;
      const status = error?.response?.status;
      if (
        status === 401 ||
        status === 403 ||
        message === "Invalid token." ||
        message === "Invalid or expired token." ||
        message === "Access denied, no token provided." ||
        message === "Access denied. Applicant token required."
      ) {
        localStorage.removeItem("portalToken");
        window.location.href = "/portal/login";
      }
      return Promise.reject(error);
    }
  );

  return client;
};

// Public Endpoints
export const portalLogin = async (email, password) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/portal/auth/login`, { email, password });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Login failed");
  }
};

export const portalForgotPassword = async (email, product = 'permit') => {
  try {
    const response = await axios.post(`${API_BASE_URL}/portal/auth/forgot-password`, { email, product });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Password reset request failed");
  }
};

export const portalResetPassword = async (token, newPassword) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/portal/auth/reset-password`, { token, newPassword });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Password reset failed");
  }
};

// Protected Endpoints
export const portalChangePassword = async (newPassword) => {
  try {
    const client = getPortalClient();
    const response = await client.post('/portal/auth/change-password', { newPassword });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Password change failed");
  }
};

export const fetchPortalDashboard = async () => {
  try {
    const client = getPortalClient();
    const response = await client.get('/portal/dashboard');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch dashboard data");
  }
};

export const fetchPortalPermitApps = async () => {
  try {
    const client = getPortalClient();
    const response = await client.get('/portal/applications/permit');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch permit applications");
  }
};

export const fetchPortalCheckApps = async () => {
  try {
    const client = getPortalClient();
    const response = await client.get('/portal/applications/check');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch check applications");
  }
};

export const fetchPortalSpectApps = async () => {
  try {
    const client = getPortalClient();
    const response = await client.get('/portal/applications/spect');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch spect applications");
  }
};

export const fetchPortalPermitApp = async (id) => {
  try {
    const client = getPortalClient();
    const response = await client.get(`/portal/application/permit/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch permit application");
  }
};

export const updatePortalPermitApp = async (id, data) => {
  try {
    const client = getPortalClient();
    const response = await client.put(`/portal/application/permit/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to update permit application");
  }
};

export const fetchPortalCheckApp = async (id) => {
  try {
    const client = getPortalClient();
    const response = await client.get(`/portal/application/check/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch plan review request");
  }
};

export const updatePortalCheckApp = async (id, data) => {
  try {
    const client = getPortalClient();
    const response = await client.put(`/portal/application/check/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to update plan review request");
  }
};

export const fetchPortalSpectApp = async (id) => {
  try {
    const client = getPortalClient();
    const response = await client.get(`/portal/application/spect/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to fetch inspection request");
  }
};

export const updatePortalSpectApp = async (id, data) => {
  try {
    const client = getPortalClient();
    const response = await client.put(`/portal/application/spect/${id}`, data);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to update inspection request");
  }
};

export const downloadPortalPermitAppPdf = async (id) => {
  try {
    const client = getPortalClient();
    const response = await client.get(`/portal/application/permit/${id}/pdf`, {
      responseType: 'blob',
    });
    const link = document.createElement('a');
    const url = window.URL.createObjectURL(new Blob([response.data]));
    link.href = url;
    link.setAttribute('download', `application_${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    throw new Error(error.response?.data?.message || "Failed to download PDF");
  }
};
